/**
 * CIPAA Election Service
 *
 * Service for managing CIPAA elections, candidates, voting, and tokens.
 *
 * @version 2025.1.0
 */

import apiClient from './apiClient';
import {
  Cipaa,
  CipaaCandidate,
  CipaaToken,
  CipaaListResponse,
  CipaaFilters,
  CreateCipaaRequest,
  UpdateCipaaRequest,
  CipaaElectionResults,
  CipaaVoterInfo,
  PublicElectionInfo,
  PublicCandidateInfo,
  TokenValidationResult,
  CpfValidationResult,
  VoteRequest,
} from '../types/cipaa';

/**
 * Extract data from API response
 * Backend returns: { message: "...", data: {...} }
 */
const extractData = (response: any) => {
  return response.data?.data || response.data;
};

/**
 * CIPAA Election Service
 */
class CipaaService {
  private baseUrl = '/cipaa';

  // ========== CRUD OPERATIONS ==========

  /**
   * Get all elections with pagination and filters
   */
  async getAll(params?: {
    page?: number;
    limit?: number;
    search?: string;
    filters?: CipaaFilters;
  }): Promise<CipaaListResponse> {
    console.log('🗳️ CipaaService.getAll - Fetching elections', params);

    const queryParams: Record<string, string | number> = {};

    if (params?.page) queryParams.page = params.page;
    if (params?.limit) queryParams.limit = params.limit;
    if (params?.search) queryParams.search = params.search;
    if (params?.filters?.status?.length) {
      queryParams.status = params.filters.status.join(',');
    }
    if (params?.filters?.companyId) {
      queryParams.companyId = params.filters.companyId;
    }

    const response = await apiClient.get(this.baseUrl, { params: queryParams });
    console.log('✅ CipaaService.getAll - Success', response.data);
    return response.data;
  }

  /**
   * Get election by ID
   */
  async getById(id: number): Promise<Cipaa> {
    console.log('🗳️ CipaaService.getById - Fetching election', id);
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    console.log('✅ CipaaService.getById - Success', response.data);
    return extractData(response);
  }

  /**
   * Create new election
   */
  async create(data: CreateCipaaRequest): Promise<Cipaa> {
    console.log('🗳️ CipaaService.create - Creating election', data);
    const response = await apiClient.post(this.baseUrl, data);
    console.log('✅ CipaaService.create - Success', response.data);
    return extractData(response);
  }

  /**
   * Update election
   */
  async update(id: number, data: UpdateCipaaRequest): Promise<Cipaa> {
    console.log('🗳️ CipaaService.update - Updating election', id, data);
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    console.log('✅ CipaaService.update - Success', response.data);
    return extractData(response);
  }

  /**
   * Delete election
   */
  async delete(id: number): Promise<void> {
    console.log('🗳️ CipaaService.delete - Deleting election', id);
    await apiClient.delete(`${this.baseUrl}/${id}`);
    console.log('✅ CipaaService.delete - Success');
  }

  // ========== STATUS TRANSITIONS ==========

  /**
   * Start registration period
   */
  async startRegistration(id: number): Promise<Cipaa> {
    console.log('🗳️ CipaaService.startRegistration', id);
    const response = await apiClient.post(`${this.baseUrl}/${id}/start-registration`);
    console.log('✅ CipaaService.startRegistration - Success');
    return extractData(response);
  }

  /**
   * Close registration period
   */
  async closeRegistration(id: number): Promise<Cipaa> {
    console.log('🗳️ CipaaService.closeRegistration', id);
    const response = await apiClient.post(`${this.baseUrl}/${id}/close-registration`);
    console.log('✅ CipaaService.closeRegistration - Success');
    return extractData(response);
  }

  /**
   * Start voting period
   */
  async startVoting(id: number): Promise<Cipaa> {
    console.log('🗳️ CipaaService.startVoting', id);
    const response = await apiClient.post(`${this.baseUrl}/${id}/start-voting`);
    console.log('✅ CipaaService.startVoting - Success');
    return extractData(response);
  }

  /**
   * Close voting period
   */
  async closeVoting(id: number): Promise<Cipaa> {
    console.log('🗳️ CipaaService.closeVoting', id);
    const response = await apiClient.post(`${this.baseUrl}/${id}/close-voting`);
    console.log('✅ CipaaService.closeVoting - Success');
    return extractData(response);
  }

  /**
   * Finalize election and determine winners
   */
  async finalize(id: number): Promise<Cipaa> {
    console.log('🗳️ CipaaService.finalize', id);
    const response = await apiClient.post(`${this.baseUrl}/${id}/finalize`);
    console.log('✅ CipaaService.finalize - Success');
    return extractData(response);
  }

  // ========== CANDIDATES ==========

  /**
   * Get all candidates for an election
   */
  async getCandidates(electionId: number): Promise<CipaaCandidate[]> {
    console.log('🗳️ CipaaService.getCandidates', electionId);
    const response = await apiClient.get(`${this.baseUrl}/${electionId}/candidates`);
    console.log('✅ CipaaService.getCandidates - Success');
    return extractData(response);
  }

  /**
   * Add candidate to election
   */
  async addCandidate(electionId: number, employeeId: number): Promise<CipaaCandidate> {
    console.log('🗳️ CipaaService.addCandidate', electionId, employeeId);
    const response = await apiClient.post(`${this.baseUrl}/${electionId}/candidates`, {
      employeeId,
    });
    console.log('✅ CipaaService.addCandidate - Success');
    return extractData(response);
  }

  /**
   * Remove candidate from election
   */
  async removeCandidate(electionId: number, candidateId: number): Promise<void> {
    console.log('🗳️ CipaaService.removeCandidate', electionId, candidateId);
    await apiClient.delete(`${this.baseUrl}/${electionId}/candidates/${candidateId}`);
    console.log('✅ CipaaService.removeCandidate - Success');
  }

  /**
   * Approve candidate
   */
  async approveCandidate(electionId: number, candidateId: number): Promise<CipaaCandidate> {
    console.log('🗳️ CipaaService.approveCandidate', electionId, candidateId);
    const response = await apiClient.post(
      `${this.baseUrl}/${electionId}/candidates/${candidateId}/approve`
    );
    console.log('✅ CipaaService.approveCandidate - Success');
    return extractData(response);
  }

  /**
   * Reject candidate
   */
  async rejectCandidate(electionId: number, candidateId: number): Promise<CipaaCandidate> {
    console.log('🗳️ CipaaService.rejectCandidate', electionId, candidateId);
    const response = await apiClient.post(
      `${this.baseUrl}/${electionId}/candidates/${candidateId}/reject`
    );
    console.log('✅ CipaaService.rejectCandidate - Success');
    return extractData(response);
  }

  // ========== TOKENS ==========

  /**
   * Get all tokens for an election
   */
  async getTokens(electionId: number): Promise<CipaaToken[]> {
    console.log('🗳️ CipaaService.getTokens', electionId);
    const response = await apiClient.get(`${this.baseUrl}/${electionId}/tokens`);
    console.log('✅ CipaaService.getTokens - Success');
    return extractData(response);
  }

  /**
   * Generate new token for candidature
   */
  async generateToken(electionId: number): Promise<CipaaToken> {
    console.log('🗳️ CipaaService.generateToken', electionId);
    const response = await apiClient.post(`${this.baseUrl}/${electionId}/tokens`);
    console.log('✅ CipaaService.generateToken - Success');
    return extractData(response);
  }

  // ========== RESULTS ==========

  /**
   * Get election results
   */
  async getResults(electionId: number): Promise<CipaaElectionResults> {
    console.log('🗳️ CipaaService.getResults', electionId);
    const response = await apiClient.get(`${this.baseUrl}/${electionId}/results`);
    console.log('✅ CipaaService.getResults - Success');
    return extractData(response);
  }

  /**
   * Get list of voters
   */
  async getVoters(electionId: number): Promise<CipaaVoterInfo[]> {
    console.log('🗳️ CipaaService.getVoters', electionId);
    const response = await apiClient.get(`${this.baseUrl}/${electionId}/voters`);
    console.log('✅ CipaaService.getVoters - Success');
    return extractData(response);
  }

  /**
   * Get elected members
   */
  async getElected(electionId: number): Promise<{
    effective: CipaaCandidate[];
    alternate: CipaaCandidate[];
  }> {
    console.log('🗳️ CipaaService.getElected', electionId);
    const response = await apiClient.get(`${this.baseUrl}/${electionId}/elected`);
    console.log('✅ CipaaService.getElected - Success');
    return extractData(response);
  }

  // ========== COMMUNICATION ==========

  /**
   * Notify union about election
   */
  async notifyUnion(electionId: number): Promise<Cipaa> {
    console.log('🗳️ CipaaService.notifyUnion', electionId);
    const response = await apiClient.post(`${this.baseUrl}/${electionId}/notify-union`);
    console.log('✅ CipaaService.notifyUnion - Success');
    return extractData(response);
  }

  // ========== PDFs ==========

  /**
   * Download candidates list PDF
   * Note: httpClient.get() already returns response.data, which is the blob itself
   */
  async downloadCandidatesPdf(electionId: number): Promise<Blob> {
    console.log('🗳️ CipaaService.downloadCandidatesPdf', electionId);
    const blob = await apiClient.get(`${this.baseUrl}/${electionId}/pdf/candidates`, {
      responseType: 'blob',
    });
    console.log('✅ CipaaService.downloadCandidatesPdf - Success');
    return blob as unknown as Blob;
  }

  /**
   * Download elected members PDF
   */
  async downloadElectedPdf(electionId: number): Promise<Blob> {
    console.log('🗳️ CipaaService.downloadElectedPdf', electionId);
    const blob = await apiClient.get(`${this.baseUrl}/${electionId}/pdf/elected`, {
      responseType: 'blob',
    });
    console.log('✅ CipaaService.downloadElectedPdf - Success');
    return blob as unknown as Blob;
  }

  /**
   * Download attendance/voters PDF
   */
  async downloadAttendancePdf(electionId: number): Promise<Blob> {
    console.log('🗳️ CipaaService.downloadAttendancePdf', electionId);
    const blob = await apiClient.get(`${this.baseUrl}/${electionId}/pdf/attendance`, {
      responseType: 'blob',
    });
    console.log('✅ CipaaService.downloadAttendancePdf - Success');
    return blob as unknown as Blob;
  }

  /**
   * Download union notification PDF (Ofício)
   */
  async downloadUnionNotificationPdf(electionId: number): Promise<Blob> {
    console.log('🗳️ CipaaService.downloadUnionNotificationPdf', electionId);
    const blob = await apiClient.get(`${this.baseUrl}/${electionId}/pdf/union-notification`, {
      responseType: 'blob',
    });
    console.log('✅ CipaaService.downloadUnionNotificationPdf - Success');
    return blob as unknown as Blob;
  }

  // ========== PUBLIC ROUTES (NO AUTH) ==========

  /**
   * Validate candidature token (public)
   */
  async validateToken(token: string): Promise<TokenValidationResult> {
    console.log('🗳️ CipaaService.validateToken (public)', token);
    const response = await apiClient.get(`${this.baseUrl}/public/token/${token}`);
    console.log('✅ CipaaService.validateToken - Success');
    return extractData(response);
  }

  /**
   * Validate CPF for candidature via token (public)
   */
  async validateCpf(token: string, cpf: string): Promise<CpfValidationResult> {
    console.log('🗳️ CipaaService.validateCpf (public)', token, cpf);
    const response = await apiClient.get(`${this.baseUrl}/public/token/${token}/validate-cpf`, {
      params: { cpf },
    });
    console.log('✅ CipaaService.validateCpf - Success');
    return extractData(response);
  }

  /**
   * Register candidature via token (public)
   */
  async registerCandidature(token: string, cpf: string): Promise<CipaaCandidate> {
    console.log('🗳️ CipaaService.registerCandidature (public)', token);
    const response = await apiClient.post(`${this.baseUrl}/public/candidature`, {
      token,
      cpf,
    });
    console.log('✅ CipaaService.registerCandidature - Success');
    return extractData(response);
  }

  /**
   * Validate CPF for candidature via accessKey (public)
   */
  async validateCpfByKey(key: string, cpf: string): Promise<CpfValidationResult> {
    console.log('🗳️ CipaaService.validateCpfByKey (public)', key, cpf);
    const response = await apiClient.get(`${this.baseUrl}/public/election/${key}/validate-cpf`, {
      params: { cpf },
    });
    console.log('✅ CipaaService.validateCpfByKey - Success');
    return extractData(response);
  }

  /**
   * Register candidature via accessKey (public)
   */
  async registerCandidatureByKey(key: string, cpf: string): Promise<CipaaCandidate> {
    console.log('🗳️ CipaaService.registerCandidatureByKey (public)', key);
    const response = await apiClient.post(`${this.baseUrl}/public/election/${key}/candidature`, {
      cpf,
    });
    console.log('✅ CipaaService.registerCandidatureByKey - Success');
    return extractData(response);
  }

  /**
   * Get election by access key (public)
   */
  async getElectionByKey(key: string): Promise<PublicElectionInfo> {
    console.log('🗳️ CipaaService.getElectionByKey (public)', key);
    const response = await apiClient.get(`${this.baseUrl}/public/election/${key}`);
    console.log('✅ CipaaService.getElectionByKey - Success');
    return extractData(response);
  }

  /**
   * Get candidates for voting (public)
   */
  async getCandidatesForVoting(key: string): Promise<PublicCandidateInfo[]> {
    console.log('🗳️ CipaaService.getCandidatesForVoting (public)', key);
    const response = await apiClient.get(`${this.baseUrl}/public/election/${key}/candidates`);
    console.log('✅ CipaaService.getCandidatesForVoting - Success');
    return extractData(response);
  }

  /**
   * Check if CPF has voted (public)
   */
  async checkVoted(key: string, cpf: string): Promise<{ hasVoted: boolean }> {
    console.log('🗳️ CipaaService.checkVoted (public)', key);
    const response = await apiClient.get(`${this.baseUrl}/public/election/${key}/check-voted`, {
      params: { cpf },
    });
    console.log('✅ CipaaService.checkVoted - Success');
    return extractData(response);
  }

  /**
   * Register vote (public)
   */
  async registerVote(data: VoteRequest): Promise<{ success: boolean; message: string }> {
    console.log('🗳️ CipaaService.registerVote (public)', data.electionKey);
    const response = await apiClient.post(`${this.baseUrl}/public/vote`, data);
    console.log('✅ CipaaService.registerVote - Success');
    return extractData(response);
  }
}

export const cipaaService = new CipaaService();
export default cipaaService;
